import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { Trophy, Flame, ChartLineUp, PokerChip, ArrowRight, Star, Quotes, Envelope, MapPin, CalendarBlank, Clock, Users, PlayCircle, X } from "@phosphor-icons/react";
import { Dialog, DialogContent } from "../components/ui/dialog";
import { listBootcamps, listModules, seedDemo } from "../lib/api";
import ReserveDialog from "../components/ReserveDialog";
import LeadDialog from "../components/LeadDialog";

// 2026 WSOP Main Event highlights — YouTube Shorts (vertical)
const TRAILERS = [
  { id: "l6vTl0y6JFM", label: "The Champion", cap: 14 },
  { id: "0PkK9pIgIfM", label: "The Winning Hand", cap: 30 },
];

const tierColor = {
  fundamentals: "border-l-felt",
  "deep-work": "border-l-gold-400",
  "q-and-a": "border-l-scarcity",
};

function BootCampCard({ session, onReserve, featured }) {
  const seatsLeft = session.capacity - session.seats_taken;
  const isUnlimited = session.capacity >= 100;
  const pct = (session.seats_taken / session.capacity) * 100;
  const isScarce = !isUnlimited && seatsLeft <= 5;
  return (
    <div
      data-testid={`bootcamp-card-${session.id}`}
      className={`relative bg-obsidian-700 border border-white/5 border-l-4 ${tierColor[session.tier] || "border-l-white/20"} p-6 transition-all hover:-translate-y-1 hover:shadow-[0_10px_30px_rgba(197,160,89,0.1)] ${featured ? "ring-1 ring-gold-400/60" : ""}`}
    >
      {featured && (
        <div className="absolute -top-3 left-6 bg-gold-400 text-black px-3 py-1 font-bebas text-xs tracking-widest">
          MOST POPULAR
        </div>
      )}
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-xs font-mono uppercase tracking-widest text-zinc-500">{session.tier}</div>
          <h3 className="font-bebas text-3xl mt-1 uppercase leading-none">{session.title}</h3>
        </div>
        {isScarce && (
          <span
            data-testid={`seats-scarce-${session.id}`}
            className="text-scarcity border border-scarcity/40 bg-scarcity/10 px-3 py-1 text-xs font-mono uppercase tracking-widest animate-pulse-glow"
          >
            {seatsLeft} left
          </span>
        )}
      </div>
      <p className="text-zinc-400 mt-3 text-sm leading-relaxed">{session.description}</p>
      <div className="grid grid-cols-3 gap-2 mt-5 text-xs font-mono">
        <div>
          <div className="text-zinc-500 uppercase tracking-widest">Next</div>
          <div className="text-white mt-1">{new Date(session.starts_at).toLocaleDateString(undefined, { month: "short", day: "numeric" })}</div>
        </div>
        <div>
          <div className="text-zinc-500 uppercase tracking-widest">Length</div>
          <div className="text-white mt-1">{session.duration_minutes} min</div>
        </div>
        <div>
          <div className="text-zinc-500 uppercase tracking-widest">Price</div>
          <div className="text-gold-400 mt-1">${session.price_usd}</div>
        </div>
      </div>
      {!isUnlimited && (
        <div className="mt-5">
          <div className="h-1 w-full bg-white/5">
            <div className={`h-full ${isScarce ? "bg-scarcity" : "bg-gold-400"}`} style={{ width: `${pct}%` }} />
          </div>
          <div className="flex justify-between mt-2 text-xs font-mono text-zinc-500">
            <span>{session.seats_taken}/{session.capacity} seats taken</span>
          </div>
        </div>
      )}
      {isUnlimited && (
        <div className="mt-5 text-xs font-mono text-zinc-500">Unlimited registrations · replay for everyone</div>
      )}
      <Button
        onClick={() => onReserve(session)}
        disabled={!isUnlimited && seatsLeft === 0}
        data-testid={`reserve-btn-${session.id}`}
        className="w-full mt-5 bg-gradient-to-r from-gold-400 to-gold-500 text-black hover:brightness-110 rounded-none font-bebas text-lg tracking-wider disabled:opacity-50"
      >
        {!isUnlimited && seatsLeft === 0 ? "Sold Out" : isUnlimited ? "Register" : "Reserve a Seat"}
      </Button>
    </div>
  );
}

function ModuleCard({ mod, onEnroll }) {
  const numStr = String(mod.module_number).padStart(2, "0");
  return (
    <div
      data-testid={`module-card-${mod.id}`}
      className="bg-obsidian-700 border border-white/5 p-3 h-full flex flex-col transition-all hover:-translate-y-1 hover:border-gold-400/40 group"
    >
      <div className="font-bebas text-3xl text-gold-400/70 leading-none group-hover:text-gold-400 transition-colors">
        {numStr}
      </div>
      <h3 className="font-bebas text-base mt-2 uppercase leading-none">{mod.title}</h3>
      <p className="text-zinc-400 mt-2 text-xs leading-snug">{mod.description}</p>
    </div>
  );
}

const TESTIMONIALS = [
  {
    name: "Marcus D.",
    role: "Home game grinder → live $2/$5",
    city: "Portland, OR",
    stake: "$2/$5 NLH",
    photo: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=200&h=200&fit=crop&crop=faces&auto=format&q=80",
    quote: "Lucas cut my leaks in half in a weekend. The preflop chart alone paid for the Boot Camp.",
  },
  {
    name: "Priya S.",
    role: "Recreational player",
    city: "Austin, TX",
    stake: "$1/$2 NLH · Home games",
    photo: "https://images.unsplash.com/photo-1544005313-94ddf0286df2?w=200&h=200&fit=crop&crop=faces&auto=format&q=80",
    quote: "POKER 101 didn't talk down to me. Three modules in and I finally understand pot odds without a calculator.",
  },
  {
    name: "Trey W.",
    role: "Tournament regular",
    city: "Las Vegas, NV",
    stake: "$400–$1,100 MTTs",
    photo: "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=200&h=200&fit=crop&crop=faces&auto=format&q=80",
    quote: "The postflop replayer in the simulator is basically homework you actually want to do.",
  },
];

const FAQS = [
  { q: "Do I need any experience to join?", a: "POKER 101 is designed for absolute beginners. Boot Camp assumes you've played at least a few hundred hands. The Live Q&A is open to anyone — brand-new players welcome." },
  { q: "What if I miss a live Boot Camp?", a: "Every session is recorded and yours to keep. Replays go out within 24 hours." },
  { q: "How do I get the Signature Cap?", a: "Reserve one from the merch section. Only 500 are being made — each numbered and signed under the brim. We ship worldwide within five business days." },
  { q: "Is Lucas actually teaching every class?", a: "Yes. Every Boot Camp is taught live by Lucas, camera on. No stand-ins, no staff coaches, no recordings." },
];

const ACES_STATS = [
  { label: "1 opponent", value: "85%" },
  { label: "2 opponents", value: "73%" },
  { label: "4 opponents", value: "56%" },
  { label: "8 opponents", value: "31%" },
];

const STYLE_TRAITS = [
  { title: "Shift gears constantly", body: "He took the lead with 13 players left and never gave it back — tightening and opening up before anyone could pin a range on him." },
  { title: "Own the preflop war", body: "Coaches point to his read on preflop domination — when to two-bet, when to three-bet, and who folds to it." },
  { title: "Give away nothing", body: "Friends say they've seen him grin once or twice across an entire run. He never breaks character at the table." },
  { title: "Play without fear", body: "A river diamond briefly threatened his lead heads-up. He shrugged it off and closed the tournament in 17 hands." },
];

const CHAMPION_HANDS = [
  { day: "Day 5 · Level 23", title: "Spikes a jack to survive", body: "All in and dominated with A♠J♠ against ace-king. A jack on the turn keeps him in the tournament." },
  { day: "Day 6 · Level 26", title: "Set of tens, check-called down", body: "Flops a set out of the big blind and lets Stavila bet all three streets with top two pair." },
  { day: "Day 7 · Level 30", title: "Kings bust Cermeno", body: "Jams over a three-bet with pocket kings, gets called by queens, and rivers a set for good measure." },
  { day: "Day 7 · Level 34", title: "Broadway, three barrels", body: "Q♠T♠ makes the nut straight on the turn. He fires 8,200,000 on the river and gets paid." },
  { day: "Day 8 · Hand of the tournament", title: "Four-bet rip vs. Givens", body: "Blind versus blind, he four-bets 27,800,000 with ace-ten into ace-queen — then flops trip tens." },
  { day: "Day 8 · Level 37", title: "Jacks full, 35,000,000 river", body: "Flops a set with pocket jacks against Trayner's queens and finds a maximum river bet. Tank-called." },
];

export default function Landing() {
  const [bootcamps, setBootcamps] = useState([]);
  const [modules, setModules] = useState([]);
  const [reserveOpen, setReserveOpen] = useState(false);
  const [reserveItem, setReserveItem] = useState(null);
  const [reserveKind, setReserveKind] = useState("bootcamp");
  const [trailerOpen, setTrailerOpen] = useState(false);
  const [activeTrailer, setActiveTrailer] = useState(TRAILERS[0]);
  const openTrailer = (t) => { setActiveTrailer(t); setTrailerOpen(true); };
  const [lead, setLead] = useState({ open: false, source: "", title: "", eyebrow: "", cta: "" });
  const openLead = (source, title, eyebrow, cta) => setLead({ open: true, source, title, eyebrow, cta });

  const refresh = async () => {
    try {
      let bc = await listBootcamps();
      let mods = await listModules();
      if (bc.length === 0 || mods.length === 0) {
        await seedDemo();
        bc = await listBootcamps();
        mods = await listModules();
      }
      setBootcamps(bc);
      setModules(mods);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => { refresh(); }, []);

  const onReserveBootcamp = (session) => { setReserveItem(session); setReserveKind("bootcamp"); setReserveOpen(true); };
  const onEnrollModule = (mod) => { setReserveItem(mod); setReserveKind("module"); setReserveOpen(true); };

  return (
    <div className="grain">
      {/* Hero */}
      <section className="relative min-h-[95vh] flex items-center overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(197,160,89,0.15),transparent_60%)]" />
        <div className="relative z-10 max-w-7xl mx-auto px-6 py-24 grid lg:grid-cols-12 gap-10 items-center w-full">
          <div className="lg:col-span-7 order-2 lg:order-1">
            <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-zinc-400 font-mono text-[11px] uppercase tracking-[0.3em] mb-6">
              <span className="flex items-center gap-2 text-zinc-400"><MapPin size={12} /> Horseshoe Las Vegas · Aug 5, 2026</span>
            </div>
            <h1 className="font-bebas text-6xl sm:text-7xl lg:text-[7rem] leading-[0.9] uppercase tracking-tight">
              Wire to wire.<br />
              <span className="text-gold-400">Ten million</span><br />
              dollars.
            </h1>
            <p className="text-lg text-zinc-300 mt-8 max-w-xl leading-relaxed">
              At 22, Lucas Jumalon held the chip lead of the 2026 WSOP Main Event from
              hand one to the last river — outlasting 9,208 players. Now Ambassador Lucas
              is ready to teach the fundamentals across the globe.
            </p>
            {/* Championship stats */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-12">
              {[
                { label: "First place", value: "$10M" },
                { label: "Entries outlasted", value: "9,208", bold: true },
                { label: "Champion age", value: "22" },
                { label: "Washington", value: "First Main Event winner born in the state", small: true },
              ].map((s) => (
                <div key={s.label} className="border-l-2 border-gold-400 pl-3">
                  <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">{s.label}</div>
                  <div
                    className={`font-bebas text-white mt-1 leading-none ${s.bold ? "font-black" : ""} ${s.small ? "text-base sm:text-lg leading-snug" : "text-2xl sm:text-3xl"}`}
                    style={s.bold ? { fontWeight: 900 } : undefined}
                  >
                    {s.value}
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="lg:col-span-5 order-1 lg:order-2 relative flex flex-col items-center lg:items-start lg:-mt-[150px]">
            <div className="relative w-1/2 lg:w-full max-w-[420px]">
              <div className="absolute -inset-6 bg-[radial-gradient(circle_at_center,rgba(255,51,102,0.2),transparent_60%)] blur-2xl" />
              <img
                src="/images/lucas-portrait.png"
                alt="Lucas Jumalon — 2026 WSOP Main Event Champion"
                className="relative w-full h-auto object-contain drop-shadow-[0_20px_50px_rgba(0,0,0,0.6)]"
                data-testid="hero-portrait"
              />
              {/* Clean caption */}
              <div className="mt-5 text-center lg:text-left border-t border-gold-400/40 pt-4">
                <div className="font-bebas uppercase tracking-wide text-white leading-none" style={{ fontSize: "21.5px" }}>Lucas Jumalon</div>
              <div className="font-mono uppercase tracking-[0.25em] text-gold-400 mt-2" style={{ fontSize: "13.5px" }}>
                2026 WSOP Main Event Champion
              </div>
              </div>
              {/* Second trailer button row */}
              <div className="mt-5 flex flex-wrap gap-2 justify-center lg:justify-start">
                {TRAILERS.map((t, i) => (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => openTrailer(t)}
                    data-testid={`hero-trailer-${i}`}
                    className="group flex items-center gap-2 bg-black/70 backdrop-blur-md border border-gold-400/60 px-3 py-1.5 hover:bg-black/90 hover:border-gold-400 transition-colors"
                    aria-label={`Play highlight — ${t.label}`}
                  >
                    <div className="w-6 h-6 rounded-full bg-gold-400 text-black flex items-center justify-center group-hover:scale-110 transition-transform">
                      <PlayCircle size={16} weight="fill" />
                    </div>
                    <div className="text-left">
                      <div className="text-[8px] font-mono uppercase tracking-widest text-gold-400 leading-none">Short {String(i + 1).padStart(2, "0")}</div>
                      <div className="font-bebas uppercase leading-none mt-0.5" style={{ fontSize: "12px" }}>{t.label}</div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Sponsor / press strip */}
      <section className="border-y border-white/5 bg-black" data-testid="sponsor-strip">
        <div className="max-w-7xl mx-auto px-6 py-6 flex flex-wrap items-center justify-center gap-x-10 gap-y-4">
          <span className="text-[10px] font-mono uppercase tracking-[0.3em] text-zinc-600">As covered by</span>
          {[
            { name: "Card Player", sub: "The Poker Authority", href: "https://www.cardplayer.com/poker-news/tournaments/wsop" },
            { name: "WSOP", sub: "World Series of Poker", href: "https://www.wsop.com/tournaments/results/?aid=2&grid=2116" },
            { name: "Horseshoe", sub: "Las Vegas", href: "https://www.caesars.com/horseshoe-las-vegas" },
            { name: "PokerNews", sub: "Live Reporting", href: "https://www.pokernews.com/tours/wsop/2026-wsop/" },
            { name: "Card Player TV", sub: "Feature Table", href: "https://www.cardplayer.com/poker-videos" },
          ].map((s) => (
            <a
              key={s.name}
              href={s.href}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`sponsor-${s.name.replace(/\s+/g, "-").toLowerCase()}`}
              className="flex flex-col items-center opacity-60 hover:opacity-100 transition-opacity focus:outline-none focus:opacity-100"
            >
              <span className="font-bebas text-2xl uppercase tracking-widest text-white leading-none group-hover:text-gold-400">{s.name}</span>
              <span className="text-[9px] font-mono uppercase tracking-[0.25em] text-zinc-600 mt-1">{s.sub}</span>
            </a>
          ))}
        </div>
      </section>

      {/* WSOP 2026 credentials band */}
      <section className="relative overflow-hidden border-y border-gold-400/20 bg-obsidian-800">
        <div className="max-w-7xl mx-auto px-6 py-20 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-6 relative">
            <div className="absolute -inset-6 bg-[radial-gradient(circle_at_center,rgba(197,160,89,0.25),transparent_70%)] blur-2xl" />
            <img
              src="/images/wsop-bracelet.png"
              alt="WSOP 2026 Main Event Championship Bracelet"
              data-testid="wsop-bracelet-image"
              className="relative w-full h-auto object-contain drop-shadow-[0_20px_50px_rgba(212,175,55,0.35)]"
            />
          </div>
          <div className="lg:col-span-6">
            <div className="flex items-center gap-3 text-gold-400 font-mono text-xs uppercase tracking-[0.3em] mb-4">
              <Trophy size={16} weight="fill" /> WORLD CHAMPION OF POKER 2026
            </div>
            <h2 className="font-bebas text-5xl sm:text-6xl uppercase leading-none">
              Bracelet <span className="text-gold-400">earned.</span>
            </h2>
            <p className="text-zinc-200 mt-6 leading-relaxed max-w-lg">
              Lucas was eight years old at a Spokane dining room table. Fourteen years later he
              won the WSOP Main Event for $10,000,000.
            </p>
            <p className="text-zinc-200 mt-4 leading-relaxed max-w-lg">
              4th-largest field ever. Second-youngest champion of all time. 17 hands to close it out
              heads-up. This isn't recycled — every drill starts with <span className="text-gold-400">POKER 101</span>.
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mt-8">
              {[
                { label: "Field size", value: "9,208" },
                { label: "Heads-up", value: "17 hands" },
                { label: "Wire-to-wire", value: "Day 1 → 8" },
              ].map((s) => (
                <div key={s.label} className="border-l-2 border-gold-400 pl-4">
                  <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">{s.label}</div>
                  <div className="font-bebas text-3xl text-white mt-1 leading-none">{s.value}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Value pillars */}
      <section className="max-w-7xl mx-auto px-6 py-24 relative z-10">
        <div className="mb-8 flex justify-center">
          <div className="w-full max-w-2xl bg-obsidian-700 border border-white/5 border-l-4 border-l-scarcity p-6">
            <div className="text-xs font-mono uppercase tracking-widest text-scarcity flex items-center gap-2 mb-2">
              <Flame size={12} weight="fill" /> RULE ZERO
            </div>
            <p className="text-zinc-200 font-manrope leading-relaxed">
              Fold more hands than feels right. The biggest leak for every new player is playing too many of them.
            </p>
          </div>
        </div>
        <div className="grid md:grid-cols-3 gap-6">
          {[
            { icon: PokerChip, title: "Poker 101", body: "Four fundamentals, taught by the 2026 World Champion. Start where he started." },
            { icon: Flame, title: "Poker Lucas Style", body: "Spokane home games to World Champion. Terrified — and fearless anyway." },
            { icon: Users, title: "Poker Boot Camp", body: "Live virtual classes, twice a week. Camera on, seats capped, replays yours to keep." },
          ].map((p, i) => (
            <div key={i} className="bg-obsidian-700 border border-white/5 p-8 hover:border-gold-400/30 transition-colors">
              <p.icon size={32} weight="duotone" className="text-gold-400" />
              <h3 className="font-bebas text-2xl uppercase mt-4">{p.title}</h3>
              <p className="text-zinc-400 mt-3 text-sm leading-relaxed">{p.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* POKER 101 */}
      <section id="poker-101" className="max-w-7xl mx-auto px-6 py-24">
        <div className="mb-10 -mt-[120px]">
          <div className="inline-flex items-center gap-2 border border-[#5B1522] bg-[#722F37] px-4 py-1.5 text-gold-400 font-bebas text-lg tracking-widest uppercase">
            <PokerChip size={16} weight="fill" /> POKER 101
          </div>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
          {modules.map((m) => (
            <ModuleCard key={m.id} mod={m} onEnroll={onEnrollModule} />
          ))}
        </div>
      </section>

      {/* Poker Lucas Style */}
      <section id="lucas-style" className="max-w-7xl mx-auto px-6 py-24">
        <div className="mb-10 -mt-[105px]">
          <div className="inline-flex items-center gap-2 border border-[#5B1522] bg-[#722F37] px-4 py-1.5 text-gold-400 font-bebas text-lg tracking-widest uppercase">
            <Flame size={16} weight="fill" /> POKER LUCAS STYLE
          </div>
        </div>
        <div className="grid lg:grid-cols-12 gap-10 mt-4">
          <div className="lg:col-span-7">
            <h2 className="font-bebas text-5xl sm:text-6xl uppercase leading-[0.95]">
              Terrified,<br />
              and <span className="text-gold-400">fearless</span><br />
              anyway.
            </h2>
            <p className="text-zinc-300 mt-6 leading-relaxed max-w-2xl">
              It started at the dining room table in Spokane, age eight, playing with his dad's friends.
              Home games, then Lilac Lanes Bowling & Casino, then a coach and a study routine. A business
              and data analytics degree gave him the quantitative half — ranges, probability, opponent
              tendencies. On August 5, 2026 it produced a wire-to-wire world championship.
            </p>
            <blockquote className="mt-8 border-l-4 border-gold-400 pl-6 py-2">
              <Quotes size={28} weight="fill" className="text-gold-400 mb-3" />
              <p className="text-xl text-zinc-100 italic leading-relaxed max-w-xl">
                "I was terrified. Channeling Mike Tyson — he used to say he was terrified he was going to die
                in the ring before he'd go out and fight. I just channeled some of that and was pretty fearless today."
              </p>
              <div className="mt-4 text-xs font-mono uppercase tracking-widest text-zinc-500">— Lucas Jumalon, after the win</div>
            </blockquote>
          </div>
          <div className="lg:col-span-5 grid grid-cols-2 gap-2 self-start">
            {[
              { big: "7,400", label: "Chips at the end of Day 1b — second-shortest stack in his flight" },
              { big: "$180K", label: "Career live earnings before this Main Event, over 49 cashes" },
              { big: "17", label: "Hands of heads-up play to finish Saaskilahti" },
              { big: "$10M", label: "First place — second-youngest champion ever, behind only Joe Cada" },
            ].map((s) => (
              <div key={s.label} className="bg-obsidian-700 border border-white/5 p-3">
                <div className="font-bebas text-2xl text-gold-400 leading-none">{s.big}</div>
                <div className="text-[10px] text-zinc-400 mt-2 leading-snug">{s.label}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 mt-16">
          {STYLE_TRAITS.map((t) => (
            <div key={t.title} className="bg-obsidian-700 border border-white/5 border-t-2 border-t-gold-400 p-4">
              <div className="font-bebas text-lg uppercase text-white leading-tight">{t.title}</div>
              <p className="text-zinc-400 mt-2 text-sm leading-snug">{t.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Boot Camp */}
      <section id="bootcamp" className="bg-obsidian-900 py-24">
        <div className="max-w-7xl mx-auto px-6">
          <div className="mb-12">
            <div className="inline-flex items-center gap-2 border border-[#5B1522] bg-[#722F37] px-4 py-1.5 text-gold-400 font-bebas text-lg tracking-widest uppercase">
              <Users size={16} weight="fill" /> BOOT CAMP
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {[
              {
                key: "session-one",
                eyebrow: "SESSION ONE",
                title: "2-Hour Fundamentals",
                bullets: [
                  "Preflop ranges by position, built from charts you keep",
                  "Pot odds and outs math you can run at the table",
                  "Live hand quizzes — you vote, Lucas shows the answer",
                  "Recording and worksheet sent after class",
                ],
                price: "$149",
                seats: "30 seats",
                cta: "Reserve a Seat",
                featured: false,
              },
              {
                key: "session-two",
                eyebrow: "SESSION TWO",
                title: "4-Hour Deep Work",
                bullets: [
                  "Everything in the 2-hour, then postflop bet sizing and three-bet pots",
                  "Championship hands broken down by the man who played them",
                  "Submit two of your own hands for live review",
                  "Breakout drills, then a graded homework set",
                  "Private group chat for the following week",
                ],
                price: "$279",
                seats: "20 seats",
                cta: "Reserve a Seat",
                featured: true,
              },
              {
                key: "open-session",
                eyebrow: "OPEN SESSION",
                title: "Live Q&A Hour",
                bullets: [
                  "Bring one hand, one question, or just listen in",
                  "Open floor — bankroll, tilt, tournament spots, going pro",
                  "No prerequisite; brand-new players welcome",
                  "Replay posted for everyone who registers",
                ],
                price: "$99",
                seats: "Unlimited",
                cta: "Register",
                featured: false,
              },
            ].map((s) => (
              <div
                key={s.key}
                data-testid={`bootcamp-static-${s.key}`}
                className={`relative bg-obsidian-800 flex flex-col p-8 transition-all ${
                  s.featured
                    ? "border-2 border-scarcity shadow-[0_0_40px_-15px_rgba(255,51,102,0.4)] pt-10"
                    : "border border-white/10 hover:border-white/25"
                }`}
              >
                {s.featured && (
                  <div className="absolute -top-4 left-6 bg-scarcity text-white px-3 py-1 font-bebas text-sm tracking-widest uppercase">
                    Most Popular
                  </div>
                )}
                <div className={`text-xs font-mono uppercase tracking-[0.25em] ${s.featured ? "text-scarcity" : "text-zinc-500"}`}>
                  {s.eyebrow}
                </div>
                <h3 className="font-bebas text-4xl uppercase mt-3 leading-none">{s.title}</h3>
                <ul className="mt-6 space-y-3 flex-1">
                  {s.bullets.map((b, i) => (
                    <li key={i} className="text-sm text-zinc-300 flex gap-3 leading-relaxed">
                      <span className={`mt-2 shrink-0 w-1.5 h-1.5 rounded-full ${s.featured ? "bg-scarcity" : "bg-gold-400"}`} />
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
                <div className="border-t border-white/10 mt-8 pt-5 flex items-baseline justify-between">
                  <div className="font-bebas text-4xl text-white leading-none">{s.price}</div>
                  <div className={`text-xs font-mono uppercase tracking-widest ${s.featured ? "text-scarcity" : "text-zinc-500"}`}>
                    {s.seats}
                  </div>
                </div>
                <button
                  type="button"
                  data-testid={`bootcamp-cta-${s.key}`}
                  onClick={() => openLead(
                    `bootcamp-${s.key}`,
                    s.title,
                    `${s.eyebrow} · ${s.price}`,
                    s.cta,
                  )}
                  className={`mt-5 w-full py-3 font-bebas text-lg uppercase tracking-widest border transition-colors ${
                    s.featured
                      ? "bg-scarcity text-white border-scarcity hover:brightness-110"
                      : "bg-transparent text-scarcity border-scarcity/60 hover:bg-scarcity/10 hover:border-scarcity"
                  }`}
                  style={{ textDecoration: "underline", textDecorationThickness: "1px", textUnderlineOffset: "4px" }}
                >
                  {s.cta}
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="max-w-7xl mx-auto px-6 py-24">
        <div className="text-xs font-mono uppercase tracking-[0.3em] text-gold-400 mb-2 flex items-center gap-2">
          <Star size={14} weight="fill" /> STUDENT RESULTS
        </div>
        <h2 className="font-bebas text-5xl uppercase">Real players. Real wins.</h2>
        <div className="grid md:grid-cols-3 gap-6 mt-10">
          {TESTIMONIALS.map((t) => (
            <div key={t.name} data-testid={`testimonial-${t.name}`} className="bg-obsidian-700 border border-white/5 p-8 flex flex-col">
              <Quotes size={28} weight="fill" className="text-gold-400" />
              <p className="text-zinc-200 mt-4 italic leading-relaxed flex-1">"{t.quote}"</p>
              <div className="mt-6 pt-4 border-t border-white/5 flex items-center gap-4">
                <img
                  src={t.photo}
                  alt={t.name}
                  loading="lazy"
                  className="w-14 h-14 rounded-full object-cover border border-gold-400/40 grayscale contrast-125"
                />
                <div>
                  <div className="font-bebas text-xl leading-none">{t.name}</div>
                  <div className="text-xs font-mono uppercase tracking-widest text-zinc-500 mt-1.5">
                    {t.city} · {t.stake}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Merch */}
      <section id="merch" className="bg-obsidian-900 py-24 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6">
          <div className="mb-12">
            <div className="inline-flex items-center gap-2 border border-[#5B1522] bg-[#722F37] px-4 py-1.5 text-gold-400 font-bebas text-lg tracking-widest uppercase">
              <Trophy size={16} weight="fill" /> ALL ABOUT THE MERCH
            </div>
          </div>

          <div className="grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-4 relative mx-auto lg:mx-0 w-full max-w-sm">
              <div className="absolute -inset-6 bg-[radial-gradient(circle_at_center,rgba(212,175,55,0.25),transparent_65%)] blur-3xl" />
              <img
                src="/images/poker-is-my-life-cap.png"
                alt="'Poker is My Life' cap featuring the WSOP 2026 Main Event bracelet"
                data-testid="merch-cap-image"
                className="relative w-full h-auto object-contain drop-shadow-[0_25px_60px_rgba(0,0,0,0.7)]"
              />
              <div className="absolute -top-3 left-4 bg-scarcity text-white px-3 py-1 font-bebas text-sm tracking-widest uppercase">
                New Drop
              </div>
            </div>
            <div className="lg:col-span-8">
              <div className="text-xs font-mono uppercase tracking-[0.3em] text-scarcity">Signature Cap · Limited Run</div>
              <h2 className="font-bebas text-5xl sm:text-6xl uppercase mt-3 leading-[0.95]">
                Poker is<br /><span className="text-gold-400">my life.</span>
              </h2>
              <p className="text-zinc-300 mt-6 leading-relaxed max-w-lg">
                Black six-panel cap, gold embroidered wordmark, and a diamond-cut replica of the
                WSOP 2026 Main Event bracelet across the brim. Only 500 pieces — each one numbered
                and signed by Lucas.
              </p>
              <ul className="mt-6 space-y-2 text-sm text-zinc-300 max-w-md">
                {[
                  "Structured 6-panel · adjustable strap-back",
                  "Gold thread embroidery, hand-finished",
                  "Numbered 001 → 500, signed under the brim",
                  "Ships worldwide within 5 business days",
                ].map((b, i) => (
                  <li key={i} className="flex gap-3 items-start">
                    <span className="mt-2 w-1.5 h-1.5 rounded-full bg-gold-400 shrink-0" />
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
              <div className="flex items-baseline gap-4 mt-8">
                <div className="font-bebas text-5xl text-white leading-none">$89</div>
                <div className="text-xs font-mono uppercase tracking-widest text-zinc-500">USD · Shipping calculated at checkout</div>
              </div>
              <div className="flex flex-col sm:flex-row gap-3 mt-6 max-w-md">
                <button
                  type="button"
                  onClick={() => openLead("merch-cap", "Poker Is My Life Cap", "Signature Cap · $89", "Reserve Mine")}
                  data-testid="merch-cap-cta"
                  className="flex-1 text-center py-3 font-bebas text-lg uppercase tracking-widest bg-gradient-to-r from-gold-400 to-gold-500 text-black hover:brightness-110 transition"
                >
                  Reserve Mine
                </button>
                <button
                  type="button"
                  onClick={() => openLead("merch-notify", "Poker Is My Life Cap — Restock", "Notify on next drop", "Notify Me")}
                  data-testid="merch-notify-cta"
                  className="flex-1 text-center py-3 font-bebas text-lg uppercase tracking-widest border border-white/20 text-white bg-transparent hover:bg-white/5 transition"
                >
                  Notify Me
                </button>
              </div>
              <div className="mt-6 text-xs font-mono uppercase tracking-widest text-scarcity">
                117 of 500 already claimed
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="max-w-4xl mx-auto px-6 py-24">
        <h2 className="font-bebas text-5xl uppercase mb-8">Frequently asked</h2>
        <Accordion type="single" collapsible className="w-full" data-testid="faq-accordion">
          {FAQS.map((f, i) => (
            <AccordionItem key={i} value={`item-${i}`} className="border-white/10">
              <AccordionTrigger className="text-left font-bebas text-xl uppercase tracking-wide hover:text-gold-400 hover:no-underline">
                {f.q}
              </AccordionTrigger>
              <AccordionContent className="text-zinc-400 leading-relaxed">{f.a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </section>

      {/* Contact */}
      <section id="contact" className="max-w-4xl mx-auto px-6 pb-24">
        <div className="bg-obsidian-700 border border-white/5 p-10 text-center">
          <div className="text-xs font-mono uppercase tracking-[0.3em] text-gold-400 mb-4">
            Press · Coaching · Sponsorship inquiries
          </div>
          <h2 className="font-bebas text-5xl uppercase leading-none">Get in touch with Lucas</h2>
          <p className="text-zinc-400 mt-4 max-w-lg mx-auto text-sm leading-relaxed">
            Booking requests, media appearances, brand partnerships and private coaching — every note goes straight to Lucas's inbox.
          </p>
          <a
            href="mailto:lucas@lucasjumalonpoker.ai?subject=Inquiry"
            data-testid="contact-cta"
            className="mt-8 inline-flex items-center gap-3 bg-gradient-to-r from-gold-400 to-gold-500 text-black px-8 py-4 font-bebas text-xl tracking-widest uppercase hover:brightness-110 transition"
          >
            <Envelope size={22} weight="fill" /> lucas@lucasjumalonpoker.ai
          </a>
          <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-zinc-500 mt-6">
            Ferris High School · Grand Canyon University · World Champion 2026
          </div>
        </div>
      </section>

      <ReserveDialog
        open={reserveOpen}
        onOpenChange={setReserveOpen}
        item={reserveItem}
        kind={reserveKind}
        onReserved={refresh}
      />

      <LeadDialog
        open={lead.open}
        onOpenChange={(o) => setLead((l) => ({ ...l, open: o }))}
        source={lead.source}
        sourceTitle={lead.title}
        eyebrow={lead.eyebrow}
        cta={lead.cta}
      />

      {/* Trailer modal */}
      <Dialog open={trailerOpen} onOpenChange={setTrailerOpen}>
        <DialogContent className="bg-black border-gold-400/30 text-white max-w-md p-0 rounded-none" data-testid="trailer-dialog">
          <div className="flex items-center justify-between px-5 py-3 border-b border-white/5">
            <div>
              <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-gold-400">WSOP 2026 · Main Event</div>
              <div className="font-bebas text-xl uppercase leading-tight">{activeTrailer.label}</div>
            </div>
            <button
              onClick={() => setTrailerOpen(false)}
              className="text-zinc-400 hover:text-white transition-colors"
              aria-label="Close trailer"
              data-testid="trailer-close-btn"
            >
              <X size={22} />
            </button>
          </div>
          <div className="relative w-full aspect-[9/16] bg-black">
            {trailerOpen && (
              <iframe
                className="absolute inset-0 w-full h-full"
                src={`https://www.youtube.com/embed/${activeTrailer.id}?autoplay=1&rel=0&modestbranding=1&playsinline=1&start=0&end=${activeTrailer.cap}`}
                title={`Lucas Jumalon — WSOP 2026 · ${activeTrailer.label}`}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
              />
            )}
          </div>
          <a
            href={`https://youtube.com/shorts/${activeTrailer.id}`}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="trailer-youtube-fallback"
            className="block px-5 py-3 border-t border-white/5 text-center text-xs font-mono uppercase tracking-widest text-zinc-400 hover:text-gold-400 transition-colors"
          >
            Video not playing? Watch on YouTube →
          </a>
        </DialogContent>
      </Dialog>
    </div>
  );
}
